import { apiRequest, HttpMethod } from '@hqo/shared-modules/dist/api-request';
import { InfiniteData, keepPreviousData, useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';

import { useApi } from 'app/shared/contexts/api';
import { useBuilding } from 'app/shared/contexts/building';
import { ActivityFeed, ActivityItem } from 'app/data/types/activity';
import { resolveActivityFilter } from 'app/shared/consts/activity';
import { ACTIVITY_FEED_QUERY_KEY } from './use-refetch-activity-feed.hook';

interface UseActivityFeedQueryResult {
  feed: ActivityFeed;
  isLoading: boolean;
  isFetching: boolean;
  isSuccess: boolean;
  isError: boolean;
  hasPartialError: boolean;
  /**
   * How many upcoming records exist ACROSS ALL TYPES — the Upcoming tab badge. Deliberately NOT
   * narrowed by the active filter chip the way the feed list is, so it is the whole upcoming count no
   * matter which filter or tab is active. `undefined` until search-service has given a trustworthy
   * answer, so a caller can render nothing rather than a confident zero.
   */
  upcomingTotal: number | undefined;
  /** Fetch the next page for a segment. */
  loadMore: (segment: Segment) => void;
  /** Whether a segment can pull another page right now — more records exist AND nothing is in flight. */
  canLoadMore: (segment: Segment) => boolean;
  /** Whether a segment's next page is currently loading (drives the bottom spinner). */
  isLoadingMore: (segment: Segment) => boolean;
  /**
   * Whether a segment has genuinely ENDED — every page fetched, nothing in flight, nothing stale.
   *
   * Distinct from `!canLoadMore`, which is also true mid-fetch, while `keepPreviousData` holds the
   * outgoing filter's pages, and after a next-page failure that left a cursor behind. Those are
   * "not right now", not "that is all there is", and a terminal affordance drawn on them appears
   * under a list that is about to grow.
   */
  isAtEnd: (segment: Segment) => boolean;
  /**
   * Pull-to-refresh: drop back to a freshly fetched first page for this segment. Resolves when the
   * refetch settles, so a caller can drive a single indicator state machine off it — deriving "done"
   * from `isRefreshing` flipping back instead leaves a render where neither state holds, and the
   * indicator visibly collapses and re-opens.
   */
  refresh: (segment: Segment) => Promise<void>;
  /** Whether a pull-to-refresh is in flight for this segment (drives the pull-down spinner). */
  isRefreshing: (segment: Segment) => boolean;
  /** What a pull-UP at the bottom of this segment should do. */
}

/**
 * `retry` — the last next-page request failed; the pull re-attempts it (the sentinel won't, by design).
 * `caught-up` — no further pages; the pull confirms the end of the feed rather than doing nothing.
 * `none` — more pages exist and are healthy, so the sentinel is already handling it.
 */

export type Segment = 'upcoming' | 'completed';

// Retry the search read a few times so a transient blip doesn't surface as a failed feed.
const SEARCH_RETRY = 2;

// Page size for the cursor-paginated feed — small enough for smooth infinite scroll.
/*
 * 50, against search-service's own default of 25 (`@Max(10000)` is the only cap, so this is ours to
 * choose). A bigger page means fewer pages loaded for the same scroll depth, and every loaded page is
 * a request the stream sync re-reads on each frame — so this trades one larger read for several
 * smaller ones, and shortens the window in which a concurrent write can land.
 */
export const PAGE_SIZE = 50;

interface CurrentUserActivityItem {
  uuid: string;
  type: ActivityItem['type'];
  timeStamp: string;
  // Occurrence window (search-service). `startAt` drives the tile's when; `endAt` the start–end range.
  // Absent for records with no occurrence → fall back to the activity `timeStamp`.
  startAt?: string;
  endAt?: string;
  statusKey: string;
  context: ActivityItem['context'];
  details: ActivityItem['details'];
  actions?: ActivityItem['actions'];
  /** When self check-in opens for an event booking; absent when attendees may not self check in. */
  selfCheckInFrom?: string;
  /** When self-cancellation closes for a booking; absent when no deadline was published. */
  cancellableUntil?: string;
  /** The raw policy behind `cancellableUntil` — the window it describes can be disjoint. */
  cancellationPolicy?: ActivityItem['cancellationPolicy'];
  /** Presence says the booking has check-in configured; the value says whether it is still open. */
  selfCheckInStatus?: string;
  selfCheckInUntil?: string | null;
}

export interface CurrentUserActivityResponse {
  data: CurrentUserActivityItem[];
  total: number;
  limit: number;
  cursor?: string;
  segment: Segment;
}

const toActivityItem = (item: CurrentUserActivityItem): ActivityItem => {
  // The tile's "when" is the occurrence start (fallback to activity time); render a range only when the
  // occurrence has a distinct end (booking/visit) — point-in-time types (event/WO) have endAt === startAt.
  const timestamp = item.startAt ?? item.timeStamp;
  const endTimestamp = item.endAt && item.endAt !== timestamp ? item.endAt : undefined;
  return {
    id: item.uuid,
    type: item.type,
    timestamp,
    ...(endTimestamp ? { endTimestamp } : {}),
    statusKey: item.statusKey,
    context: item.context,
    details: item.details,
    actions: item.actions,
    ...(item.selfCheckInFrom ? { selfCheckInFrom: item.selfCheckInFrom } : {}),
    ...(item.cancellableUntil ? { cancellableUntil: item.cancellableUntil } : {}),
    ...(item.cancellationPolicy ? { cancellationPolicy: item.cancellationPolicy } : {}),
    ...(item.selfCheckInStatus ? { selfCheckInStatus: item.selfCheckInStatus } : {}),
    ...(item.selfCheckInUntil ? { selfCheckInUntil: item.selfCheckInUntil } : {}),
  };
};

/**
 * Fill in the building's timezone where the item carries none.
 *
 * The search projection's context is buildingUuid/tenant/landlord/portfolio and nothing else, so every
 * item arrives without a zone and renders in UTC — an Eastern building read four hours ahead. (The
 * response DTO documents a `buildingTimezone`, but nothing server-side populates it.) The feed is scoped
 * to a single building by the launch params, so that building's zone is the right answer for everything
 * in it — this is the sole source of a zone for every activity type.
 *
 * `??` so a projection that starts carrying its own zone wins rather than being overwritten.
 */
const withBuildingTimezone = (item: ActivityItem, buildingTimezone?: string): ActivityItem => (
  { ...item, context: { ...item.context, buildingTimezone: item.context.buildingTimezone ?? buildingTimezone } }
);

// Flatten the cursor pages into mapped items, deduped by id — guards against a record appearing in two
// pages if the search_after window shifts between fetches.
const flattenPages = (pages: CurrentUserActivityResponse[] | undefined): ActivityItem[] => {
  const seen = new Set<string>();
  return (pages ?? [])
    .flatMap((page) => page.data ?? [])
    .reduce<ActivityItem[]>((acc, raw) => {
      const item = toActivityItem(raw);
      if (!seen.has(item.id)) {
        seen.add(item.id);
        acc.push(item);
      }
      return acc;
    }, []);
};

// The Activity Center feed lives on search-service. In prod the shared gateway routes `/search/*` to
// it, so `apiUrl` is used as-is. Locally there is no gateway in front: `/api/*` is helix on :3000 while
// search-service runs on :3035, so rewrite only the local host. Exported so the activity stream
// (use-activity-feed-stream) addresses the same host.
export const resolveSearchUrl = (apiUrl: string): string => apiUrl.replace('localhost:3000', 'localhost:3035');

// Shared by the query itself and by `refresh`, which has to address the exact same cache entry. `locale`
// is part of the key so switching language refetches instead of serving the previous locale's cached feed.
const feedQueryKey = (segment: Segment, type: ActivityItem['type'] | undefined, searchUrl: string, locale: string) => [
  // The same constant the invalidation helper addresses. It used to be a duplicated literal here, which
  // meant renaming one side silently stopped every refetch-after-mutation from matching anything.
  ACTIVITY_FEED_QUERY_KEY,
  segment,
  type ?? 'all',
  searchUrl,
  locale,
];

interface FetchActivityPageParams {
  headers: Record<string, string>;
  searchUrl: string;
  segment: Segment;
  type?: ActivityItem['type'];
  locale: string;
  cursor?: string;
}

/**
 * One page of a segment. Extracted from the query's `queryFn` so the stream's silent sync can re-read a
 * page it already holds — at the SAME cursor — without going through the query and its loading states.
 */
export const fetchActivityPage = async ({
  headers,
  searchUrl,
  segment,
  type,
  locale,
  cursor,
}: FetchActivityPageParams): Promise<CurrentUserActivityResponse> => {
  const typeParam = type ? `&types=${type}` : '';
  const cursorParam = cursor ? `&cursor=${encodeURIComponent(cursor)}` : '';
  const localeParam = locale ? `&locale=${encodeURIComponent(locale)}` : '';
  const payload = await apiRequest<CurrentUserActivityResponse, void, void>({
    url: `${searchUrl}/search/v1/users/current/activity-events?segment=${segment}&limit=${PAGE_SIZE}${typeParam}${cursorParam}${localeParam}`,
    method: HttpMethod.GET,
    headers,
  });
  return payload.isEmpty() ? { data: [], total: 0, limit: PAGE_SIZE, cursor: undefined, segment } : payload.data;
};

// `type` narrows the fetch to a single activity type (the active filter chip); undefined = all types.
// Cursor-paginated: each page's `cursor` is the next page's param; a nullish cursor = end of list.
const useCurrentUserSegment = (segment: Segment, type?: ActivityItem['type']) => {
  const { apiUrl, getAuthHeaders } = useApi();
  // The user's locale, forwarded so search-service resolves each item's title / status label (all
  // translation-nest copy UUIDs) in the same language as the FE chrome — otherwise the feed comes back
  // in the default locale and a Hungarian user sees English titles beside Hungarian chrome.
  const { locale } = useIntl();
  const searchUrl = resolveSearchUrl(apiUrl);

  return useInfiniteQuery({
    queryKey: feedQueryKey(segment, type, searchUrl, locale),
    enabled: !!searchUrl,
    retry: SEARCH_RETRY,
    // Keep the previous pages visible while a chip/tab switch refetches — no empty flash between filters.
    // This covers a KEY change only; it has nothing to say about a refetch of the key already showing.
    placeholderData: keepPreviousData,
    /*
     * Focus does NOT refetch this feed, because for a cursor-paginated infinite query a refetch is
     * never free: TanStack re-runs the whole page chain from the top, page two fetched with a cursor
     * derived from the refetched page one. If anything changed at the head — and returning to a tab is
     * exactly when it has — the pages come back as shifted windows and `flattenPages` dedupes the
     * overlap into a list of a different length than the one on screen. Every tab refocus jumped.
     *
     * The stream is what keeps this fresh now, and it covers the case this setting was for: a
     * connection dropped while the tab was hidden reconnects on return, and a reconnect runs a catch-up
     * sync. Mount, a segment/filter switch and pull-to-refresh all still fetch normally.
     */
    refetchOnWindowFocus: false,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }): Promise<CurrentUserActivityResponse> => fetchActivityPage({
      headers: await getAuthHeaders(),
      searchUrl,
      segment,
      type,
      locale,
      cursor: pageParam,
    }),
    // "Another page exists" has to come from the page CONTENTS, never from the cursor's presence:
    // search-service derives the cursor from the last hit's `sort` values, so it returns one whenever the
    // page held at least one record — including a final, short page. Trusting it made a single-record feed
    // report `hasNextPage`, and since a one-card list leaves the sentinel on screen, that fired a phantom
    // fetch with no scrolling at all.
    getNextPageParam: (lastPage, allPages) => {
      // A partial page means the index had nothing left to give.
      if ((lastPage.data?.length ?? 0) < PAGE_SIZE) return undefined;
      // Exact-multiple guard: a full FINAL page would otherwise still cost one empty fetch to find the end.
      // Counts UNIQUE uuids, not raw records — overlapping pages (the same shifted-window overlap
      // `flattenPages` dedupes) would otherwise reach `total` while the rendered list is still short,
      // ending the feed with records missing.
      const loaded = new Set(allPages.flatMap((page) => (page.data ?? []).map((record) => record.uuid))).size;
      if (lastPage.total > 0 && loaded >= lastPage.total) return undefined;
      return lastPage.cursor ?? undefined;
    },
  });
};

/**
 * The count behind the Upcoming tab badge: how many upcoming records exist ACROSS ALL TYPES.
 *
 * Deliberately its own read rather than the upcoming feed query's `total`. The feed carries the active
 * filter chip's `types` narrow, so its total describes the filtered list on screen — but the badge is
 * meant to be the whole upcoming count, unchanged by the filter or which tab is showing. Keeping it a
 * separate query whose key OMITS the filter is what makes that structural: a chip change never refetches
 * or blanks this number.
 *
 * `limit=1` because only `total` is wanted, not the records (search-service returns the full segment
 * count regardless of page size). No `locale` param — the count is language-independent, so forwarding
 * it would only cause a needless refetch on every language switch. `undefined` until a trustworthy
 * answer lands, so the badge renders nothing rather than a premature zero.
 *
 * `refetch` is exposed so pull-to-refresh can refresh the badge alongside the list — the count is its
 * own query, so without that a refresh would update the list and leave the badge on its cached number.
 */
const useUpcomingTotal = (): { total: number | undefined; refetch: () => Promise<unknown> } => {
  const { apiUrl, getAuthHeaders } = useApi();
  const searchUrl = resolveSearchUrl(apiUrl);

  const { data, refetch } = useQuery({
    queryKey: [ACTIVITY_FEED_QUERY_KEY, 'upcoming', 'total-all-types', searchUrl],
    enabled: !!searchUrl,
    retry: SEARCH_RETRY,
    queryFn: async (): Promise<number> => {
      const headers = await getAuthHeaders();
      const payload = await apiRequest<CurrentUserActivityResponse, void, void>({
        url: `${searchUrl}/search/v1/users/current/activity-events?segment=upcoming&limit=1`,
        method: HttpMethod.GET,
        headers,
      });
      return payload.isEmpty() ? 0 : payload.data.total;
    },
  });

  return { total: data, refetch };
};

/**
 * Feed source for the Activity Center: the unified `GET /search/v1/users/current/activity-events` read,
 * one call per segment. This is the ONLY source — the interim client-side fan-out over transactions /
 * visits / work orders is gone, so a search failure surfaces as a failed feed rather than silently
 * swapping data sources underneath the user.
 *
 * Ordering and segmentation are server-side: search-service sorts each segment and homes terminal
 * statuses into Completed, so nothing here re-sorts or re-buckets the pages it receives.
 */
export const useActivityFeedQuery = (): UseActivityFeedQueryResult => {
  const queryClient = useQueryClient();
  const { apiUrl } = useApi();
  const { locale } = useIntl();
  const searchUrl = resolveSearchUrl(apiUrl);
  const { building } = useBuilding();
  const buildingTimezone = building?.timezone;
  // Which segment a pull-to-refresh is currently running for. Tracked here rather than read off the
  // query, because a refresh must NOT look like the initial load — `isLoading`/`isFetching` would put the
  // full-pane spinner back up, replacing the list the pull indicator is supposed to be sitting above.
  const [refreshingSegment, setRefreshingSegment] = useState<Segment | null>(null);
  const [searchParams] = useSearchParams();
  // The active filter chip drives a server-side type narrow (see search-service). `all` → no narrow.
  const filter = resolveActivityFilter(searchParams.get('filter'));
  const type = filter === 'all' ? undefined : filter;

  const upcoming = useCurrentUserSegment('upcoming', type);
  const completed = useCurrentUserSegment('completed', type);
  const searchQueries = [upcoming, completed];
  // The Upcoming badge count is unfiltered on purpose — its own read, independent of `type` above.
  // `refetchUpcomingTotal` lets pull-to-refresh refresh the badge with the list (see `refresh`).
  const { total: upcomingTotal, refetch: refetchUpcomingTotal } = useUpcomingTotal();

  // `every`, not `some`. Both segments are fetched but only one is on screen, so a failed Completed
  // beside a populated Upcoming used to replace a list the user could see with a full-pane error. Only
  // a feed with nothing at all to show is a failed feed.
  //
  // Deliberately `isLoadingError` (errored with no data) and not a bare `isError`: a failed NEXT page
  // still has pages on screen and must not blank the list the user already scrolled through. That case
  // is a partial error, handled below.
  const searchDown = searchQueries.every((query) => query.isLoadingError);
  // One segment down while the other answers. Not a blank pane, but not silence either — if it is the
  // segment being viewed, it reads as empty, and the banner is what says otherwise.
  const segmentDown = searchQueries.some((query) => query.isLoadingError);

  const bySegment: Record<Segment, typeof upcoming> = { upcoming, completed };
  return {
    feed: {
      upcoming: flattenPages(upcoming.data?.pages).map((item) => withBuildingTimezone(item, buildingTimezone)),
      completed: flattenPages(completed.data?.pages).map((item) => withBuildingTimezone(item, buildingTimezone)),
    },
    isLoading: searchQueries.every((query) => query.isLoading),
    isFetching: searchQueries.some((query) => query.isFetching),
    isSuccess: searchQueries.some((query) => query.isSuccess),
    isError: searchDown,
    // A next-page failure leaves pages on screen but an incomplete list, so say so through the existing
    // partial-error banner rather than silently pretending the feed ended.
    // `isFetchNextPageError` is the load-bearing half: InfiniteQueryObserver derives
    // `isRefetchError: isRefetchError && !isFetchNextPageError && !isFetchPreviousPageError`, so a failed
    // NEXT PAGE — the exact case this banner exists for — reports `isRefetchError: false`. Checking only
    // that flag meant the banner never rendered in prod. `isRefetchError` still covers the other half: a
    // failed background refetch, where the list on screen is stale rather than short.
    hasPartialError:
      segmentDown || searchQueries.some((query) => query.isFetchNextPageError || query.isRefetchError),
    upcomingTotal,
    loadMore: (segment) => {
      bySegment[segment].fetchNextPage().catch(() => undefined);
    },
    // Gated on a settled, real page as well as `hasNextPage`: `keepPreviousData` holds the OUTGOING filter's
    // pages (and therefore its `hasNextPage`) while a chip/tab switch refetches, so an ungated sentinel would
    // request a next page against the pagination state of a filter the user just left.
    canLoadMore: (segment) => {
      const query = bySegment[segment];
      // Nothing left to pull, or the pages on screen belong to the filter the user just left.
      if (!query.hasNextPage || query.isPlaceholderData) return false;
      // `!isFetchNextPageError` is load-bearing: when a next page fails, the last GOOD page still yields a
      // cursor, so `hasNextPage` stays true. Without it the sentinel — still on screen, since the failed
      // page added no content to push it down — would re-request the same failing cursor in an unbounded
      // loop. Deliberately the direction-specific flag rather than a bare `isError`: a failed BACKGROUND
      // refetch shouldn't wedge load-more, since the next page itself may be perfectly fetchable.
      return !query.isFetchNextPageError && !query.isFetching && !query.isFetchingNextPage;
    },
    isLoadingMore: (segment) => bySegment[segment].isFetchingNextPage,
    // Deliberately NOT `!canLoadMore`: that is false whenever a pull is merely unavailable, which
    // includes every in-flight and stale state. The end is the absence of a next page on pages that
    // belong to the filter on screen, with nothing outstanding that could add one.
    isAtEnd: (segment) => {
      const query = bySegment[segment];

      return !query.hasNextPage
        && !query.isPlaceholderData
        && !query.isFetching
        && !query.isFetchingNextPage
        // A bare `isError`, unlike `canLoadMore`'s direction-specific check, because the two answer
        // different questions. `canLoadMore` asks whether a pull is worth attempting, and a failed
        // BACKGROUND refetch says nothing about the next page — wedging pagination on it would
        // strand the reader. This asks whether the list is COMPLETE, and a query whose last fetch
        // failed cannot claim that: the cached pages say `hasNextPage === false`, but they are the
        // pages we already know to be stale. `hasPartialError` is telling the reader as much on the
        // same screen, so a "that is everything" underneath it would contradict it.
        && !query.isError;
    },
    refresh: (segment) => {
      setRefreshingSegment(segment);
      // Trim the cache to page 1, THEN refetch — so the refresh costs one request instead of re-requesting
      // every page the user had scrolled through. Trimming in place (rather than resetQueries) keeps the
      // existing items on screen while the request is in flight, so the list doesn't blank out under the
      // pull indicator; react-query swaps them for the fresh page when it lands.
      queryClient.setQueryData<InfiniteData<CurrentUserActivityResponse, string | undefined>>(
        feedQueryKey(segment, type, searchUrl, locale),
        (existing) => {
          if (!existing || existing.pages.length <= 1) return existing;
          return { pages: existing.pages.slice(0, 1), pageParams: existing.pageParams.slice(0, 1) };
        },
      );
      // The badge count is its OWN query, so a refresh has to refetch it too — otherwise the list
      // comes back fresh while the badge keeps its cached number and the two disagree. Cheap
      // (limit=1); refreshing it on either segment keeps it honest. Both settle before the pull
      // indicator drops.
      return Promise.all([
        bySegment[segment].refetch().catch(() => undefined),
        refetchUpcomingTotal().catch(() => undefined),
      ])
        .then(() => undefined)
        .finally(() => setRefreshingSegment(null));
    },
    isRefreshing: (segment) => refreshingSegment === segment,
  };
};
